import { useEffect, useRef, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { App, Page, Problem, Upload } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { EmptyState } from '../components/EmptyState';
import { KeyValue } from '../components/KeyValue';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { Link } from '../app/Link';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';
import type { Route, WorkspaceRoute } from '../app/router';
import {
  appStatusView,
  engineStatusView,
  isUploadSettled,
  schemaStatusView,
  uploadStatusView,
} from '../app/status';
import { formatDateTime } from '../lib/datetime';
import { DeleteDialog } from './DeleteDialog';

export interface WorkspacePageProps {
  route: WorkspaceRoute;
  navigate(route: Route, options?: { replace?: boolean }): void;
  /** Workspaceを削除し終えたときに呼ぶ。サイドバーの一覧を取り直させるため。 */
  onWorkspaceDeleted?(): void;
}

export function WorkspacePage({ route, navigate, onWorkspaceDeleted }: WorkspacePageProps) {
  const { api, mode } = usePlatform();
  const { workspaceId } = route;
  const apps = useAsync(() => api.listApps(workspaceId), [api, workspaceId]);

  return (
    <div className="page">
      {/* fixture には Studio の経路が無いので導線を出さない（C45）。 */}
      {mode === 'http' && (
        <Card title="Studio">
          <p className="hint">
            AI と対話しながら、資料から業務の流れを整理して microapp を作ります。{' '}
            <Link to={{ name: 'studio', workspaceId }} onNavigate={navigate}>
              Studio を開く
            </Link>
          </p>
        </Card>
      )}
      <Card title="知識ソース">
        <p className="hint">
          Studio の AI が読む資料です。ファイルは解析せずそのまま保存します。{' '}
          <Link to={{ name: 'knowledge', workspaceId }} onNavigate={navigate}>
            知識ソースを開く
          </Link>
        </p>
      </Card>

      <UploadPanel
        workspaceId={workspaceId}
        uploadId={route.uploadId ?? null}
        onAccepted={(upload) => navigate({ name: 'workspace', workspaceId, uploadId: upload.id }, { replace: true })}
        onSettled={apps.reload}
      />

      <Card title="microapp">
        {apps.loading && <Spinner label="microappを取得しています" />}
        <ProblemNotice problem={apps.problem} action={<Button onClick={apps.reload}>再取得</Button>} />
        {apps.data && apps.data.items.length === 0 && (
          <EmptyState
            title="microappがまだありません"
            description="ZIPを登録すると、BPMNと標準フォームから利用できるようになります。"
          />
        )}
        {apps.data && apps.data.items.length > 0 && (
          <Table
            label="microapp一覧"
            columns={[
              { key: 'name', header: '名前' },
              { key: 'appKey', header: 'appKey' },
              { key: 'status', header: '状態' },
              { key: 'ui', header: '画面' },
              { key: 'updatedAt', header: '更新' },
            ]}
            rows={apps.data.items.map((app) => {
              const status = appStatusView(app.status);
              return {
                key: app.id,
                cells: {
                  name:
                    // Draft だけの microapp は定義が無く、詳細画面では 503 になる。Studio で開く。
                    app.status === 'draft' ? (
                      <Link to={{ name: 'studio', workspaceId, appId: app.id }} onNavigate={navigate}>
                        {app.name}
                      </Link>
                    ) : (
                      <Link to={{ name: 'app', workspaceId, appId: app.id, tab: 'process' }} onNavigate={navigate}>
                        {app.name}
                      </Link>
                    ),
                  appKey: <code>{app.appKey}</code>,
                  status: (
                    <span className="status-cell">
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {status.hint && <span className="hint">{status.hint}</span>}
                    </span>
                  ),
                  ui: app.hasUi ? 'あり' : 'なし',
                  updatedAt: formatDateTime(app.updatedAt),
                },
              };
            })}
          />
        )}
      </Card>

      <DeleteWorkspacePanel
        workspaceId={workspaceId}
        apps={apps.data}
        onDeleted={() => {
          onWorkspaceDeleted?.();
          // 消したWorkspaceのURLへ「戻る」で帰れないよう、履歴を置き換えて一覧（作成画面）へ移る。
          navigate({ name: 'workspaces' }, { replace: true });
        }}
      />
    </div>
  );
}

interface DeleteWorkspacePanelProps {
  workspaceId: string;
  /** 確認ダイアログで「消えるmicroappの件数」を出すために使う。未取得ならnull。 */
  apps: Page<App> | null;
  onDeleted(): void;
}

/**
 * Workspaceの削除。確認でWorkspace名の入力を求めるので、名前はここで取得する。
 * 日常の操作（登録・閲覧）の途中で押さないよう、ページの最後に置く。
 */
function DeleteWorkspacePanel({ workspaceId, apps, onDeleted }: DeleteWorkspacePanelProps) {
  const { api } = usePlatform();
  const workspace = useAsync(() => api.getWorkspace(workspaceId), [api, workspaceId]);
  const [confirming, setConfirming] = useState(false);
  // 一覧は先頭ページしか持っていないので、続きがあれば「以上」を付けて件数を断定しない。
  const appCount = apps ? `（${apps.items.length}件${apps.nextCursor ? '以上' : ''}）` : '';

  return (
    <Card title="Workspaceの削除">
      <div className="stack">
        <p className="hint">
          このWorkspaceと、中のmicroapp・業務データ・実行履歴をすべて削除します。取り消せません。
        </p>
        <ProblemNotice
          problem={workspace.problem}
          action={<Button onClick={workspace.reload}>再取得</Button>}
        />
        <div className="row">
          <Button variant="danger" disabled={!workspace.data} onClick={() => setConfirming(true)}>
            Workspaceを削除
          </Button>
        </div>
      </div>

      {confirming && workspace.data && (
        <DeleteDialog
          kind="Workspace"
          name={workspace.data.name}
          consequences={[
            `microapp${appCount}と、そのengine配備（BPMN/DMN定義・タイマーやメッセージ開始の待ち受け・実行中のインスタンス）`,
            '保存ファイル（アップロードしたZIP・ビルド成果物・asset）',
            '業務テーブルとその中のデータ（Workspaceのデータベースschemaごと）',
            '実行履歴・Task・設定・イベント',
            'Workspace自体（名前・説明・アクセス権）',
          ]}
          onDelete={async (idempotencyKey) => {
            await api.deleteWorkspace(workspaceId, { idempotencyKey });
            onDeleted();
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </Card>
  );
}

interface UploadPanelProps {
  workspaceId: string;
  uploadId: string | null;
  onAccepted(upload: Upload): void;
  onSettled(): void;
}

/**
 * ZIP一括登録と、その進捗の表示。
 * status / schemaStatus / engineStatus は別々の軸として出す。
 * 1本の進捗に畳むと「DDLは通ったがengineは成否不明」のような状態が見えなくなる。
 */
function UploadPanel({ workspaceId, uploadId, onAccepted, onSettled }: UploadPanelProps) {
  const { api } = usePlatform();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [upload, setUpload] = useState<Upload | null>(null);
  const settledNotified = useRef<string | null>(null);

  // 進行中のuploadだけを追いかける。完了/失敗したら止める。
  useEffect(() => {
    if (!uploadId) {
      setUpload(null);
      return;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const next = await api.getUpload(workspaceId, uploadId);
        if (!active) return;
        setUpload(next);
        if (isUploadSettled(next.status)) {
          if (settledNotified.current !== next.id) {
            settledNotified.current = next.id;
            onSettled();
          }
          return;
        }
      } catch (error) {
        if (!active) return;
        setProblem(toProblem(error));
        return;
      }
      timer = setTimeout(() => void poll(), 1_000);
    };
    void poll();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [api, workspaceId, uploadId, onSettled]);

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setProblem(null);
    try {
      const accepted = await api.uploadApp(workspaceId, file, {
        fileName: file.name,
        idempotencyKey: newIdempotencyKey(),
      });
      setUpload(accepted);
      onAccepted(accepted);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="ZIPを登録する">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">bundle（ZIP）</span>
          <input
            className="input"
            type="file"
            accept=".zip,application/zip"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>
        <ProblemNotice problem={problem} />
        <div className="row">
          <Button type="submit" variant="primary" disabled={submitting || !file}>
            {submitting ? '送信中…' : '登録する'}
          </Button>
          <span className="hint">同じappKeyへの再登録は、現在の一式を置き換えます。</span>
        </div>
      </form>

      {upload && <UploadProgress upload={upload} />}
    </Card>
  );
}

function UploadProgress({ upload }: { upload: Upload }) {
  const status = uploadStatusView(upload.status);
  const schema = schemaStatusView(upload.schemaStatus);
  const engine = engineStatusView(upload.engineStatus);

  return (
    <section className="upload-progress" aria-live="polite">
      <KeyValue
        items={[
          { label: '登録', value: <Badge tone={status.tone}>{status.label}</Badge> },
          { label: 'DDL', value: <Badge tone={schema.tone}>{schema.label}</Badge> },
          { label: 'engine配備', value: <Badge tone={engine.tone}>{engine.label}</Badge> },
          { label: 'uploadId', value: <code>{upload.id}</code> },
        ]}
      />
      {/* 状態ラベルは軸をまたいで重複しうるので、前置きは軸名にする。 */}
      {(
        [
          ['登録', status],
          ['DDL', schema],
          ['engine配備', engine],
        ] as const
      )
        .filter(([, view]) => view.hint !== null)
        .map(([axis, view]) => (
          <p key={axis} className="hint">
            {axis}: {view.hint}
          </p>
        ))}

      <ProblemNotice problem={upload.error} />

      {upload.warnings.length > 0 && (
        <div className="warnings">
          <h4>警告</h4>
          <ul>
            {upload.warnings.map((warning) => (
              <li key={`${warning.code}:${warning.message}`}>
                <code>{warning.code}</code> {warning.message}
              </li>
            ))}
          </ul>
          <p className="hint">
            DROPを含む変更も適用されます。承認待ちにはならないため、内容は登録前に確認してください。
          </p>
        </div>
      )}

      <div className="ddl-plan">
        <h4>DDL計画</h4>
        {upload.ddlPlan === null ? (
          <p className="hint">
            計画はまだ取得できていません（変更が無いことを意味しません。変更なしは「DDL: 変更なし」で表します）。
          </p>
        ) : (
          <pre>{upload.ddlPlan}</pre>
        )}
      </div>
    </section>
  );
}
